import { createContext, useContext } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from './cn';
import { DropdownSelect, type DropdownSelectProps } from './DropdownSelect';
import { Select, type SelectOption } from './Select';

/**
 * Shared look for every dropdown that sits in a page's filter bar: one fixed
 * width, never shrinks, and no external text label — the "all …" option
 * (e.g. "All statuses") already says what the control filters.
 */
export const FILTER_CONTROL_CLS = 'w-40 shrink-0';

/**
 * Supplied by FilterBar around each on-demand filter so the dropdown can show
 * its own small × (inside the box) that removes the filter.
 */
export const FilterRemoveContext = createContext<{ onRemove: () => void; label: string } | null>(null);

export interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  className?: string;
  defaultOpen?: boolean;
}

/** Filter-bar dropdown over a small, static option list (status, mode, state, provider …). */
export function FilterSelect({ value, onChange, options, className, defaultOpen }: FilterSelectProps) {
  const remove = useContext(FilterRemoveContext);
  return (
    <Select
      onRemove={remove?.onRemove}
      removeLabel={remove ? `Remove ${remove.label} filter` : undefined}
      showChevron={false}
      value={value}
      onChange={onChange}
      options={options}
      defaultOpen={defaultOpen}
      className={cn(FILTER_CONTROL_CLS, className)}
    />
  );
}

export type FilterDropdownProps = Omit<DropdownSelectProps, 'className'> & { className?: string };

/** Filter-bar dropdown with a server-backed, searchable, paginated option list (endpoint, group, OS …). */
export function FilterDropdown({ className, ...props }: FilterDropdownProps) {
  const remove = useContext(FilterRemoveContext);
  return (
    <div className={cn(FILTER_CONTROL_CLS, className)}>
      <DropdownSelect
        {...props}
        onRemove={remove?.onRemove}
        removeLabel={remove ? `Remove ${remove.label} filter` : undefined}
        showChevron={false}
        className="h-auto px-2.5 py-1 text-xs" />
    </div>
  );
}

export interface ClearFiltersButtonProps {
  /** Whether any filter is currently applied. */
  active: boolean;
  onClear: () => void;
}

/**
 * "Clear filters" button for a filter bar. Always occupies its slot — it is
 * only hidden (invisible, not unmounted) while no filter is active — so the
 * bar never changes layout or wraps when the first filter is applied.
 */
export function ClearFiltersButton({ active, onClear }: ClearFiltersButtonProps) {
  return (
    <button
      type="button"
      onClick={onClear}
      disabled={!active}
      tabIndex={active ? 0 : -1}
      aria-hidden={!active}
      className={cn(
        'flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-[11px] text-text-tertiary hover:bg-surface-2 hover:text-text-primary transition-colors',
        !active && 'invisible',
      )}
    >
      <X className="h-3 w-3" />
      Clear filters
    </button>
  );
}

export interface FilterSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Override the default fixed width (e.g. "w-full" inside a side panel). */
  className?: string;
}

/** Filter-bar search box — same width as the dropdowns so every bar lines up. */
export function FilterSearch({ value, onChange, placeholder = 'Search…', className }: FilterSearchProps) {
  return (
    <div className={cn('relative flex w-56 shrink-0 items-center', className)}>
      <Search className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-text-tertiary" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-border-default bg-surface-1 py-1 pl-7 pr-2.5 text-xs text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-border-focus/20"
      />
    </div>
  );
}

/** Label of the option with the given value — used for the "OS is Windows 11" chip text. */
export function optionLabel(options: SelectOption[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}
